import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Loader2, UserCog } from 'lucide-react';
import { useImpersonation } from './ImpersonationContext';
import { projectId } from '../utils/supabase/info';
import { toast } from 'sonner';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
}

interface UserImpersonationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  companyName: string;
  accessToken: string;
}

export function UserImpersonationDialog({ isOpen, onClose, companyId, companyName, accessToken }: UserImpersonationDialogProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const { startImpersonation } = useImpersonation();
  const [impersonatingId, setImpersonatingId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && companyId) {
      loadUsers();
    }
  }, [isOpen, companyId]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      // Use existing endpoint or admin endpoint
      // We can use the admin endpoint to get users for a specific company if we filter, 
      // but better to use a specific endpoint if available.
      // For now, let's use the company data endpoint which usually includes users or we fetch them.
      // Actually, let's use the System Admin endpoint pattern but filtered.
      // Or simply fetch all users and filter (inefficient but works for now if no specific endpoint).
      // Wait, there is likely a route for this.
      // Let's try /make-server-e90c59cd/companies/:id/users
      
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${companyId}/users`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      } else {
        // Fallback: Try admin endpoint logic?
        console.error('Failed to load users');
      }
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImpersonate = async (user: User) => {
    if (!confirm(`Are you sure you want to view as ${user.email}? This action will be audited.`)) return;
    
    setImpersonatingId(user.id);
    try {
      await startImpersonation(accessToken, user.id, `Impersonating ${user.email} from Client Detail View`);
      onClose();
      // The AppRouter effect will handle the redirect/reload/state update
    } catch (error) {
      console.error('Impersonation failed:', error);
    } finally {
      setImpersonatingId(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>View As Client User</DialogTitle>
          <DialogDescription>
            Select a user from <strong>{companyName}</strong> to impersonate.
            <br />
            <span className="text-amber-600 flex items-center gap-1 mt-1">
              <UserCog className="h-3 w-3" />
              This action creates an audited session. You will see exactly what they see.
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No users found for this company.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.name || '—'}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{user.role}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        size="sm" 
                        variant="secondary"
                        onClick={() => handleImpersonate(user)}
                        disabled={!!impersonatingId}
                      >
                        {impersonatingId === user.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          'View As'
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
